import React, { useEffect, useState } from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';
import { listarTiposReembolso } from '~/Services/Service';

const parametrosSchema = (somenteLotesQuitados) =>
  Yup.object().shape({
    codigoReembolso: Yup.number()
      .required('Selecione o tipo de reembolso')
      .positive()
      .integer(),
    primeiroVencimento: somenteLotesQuitados
      ? Yup.string().required('Informe o 1º vencimento')
      : Yup.string().nullable(),
    mensagem1: Yup.string().max(100, 'Máximo 100 caracteres').nullable(),
    mensagem2: Yup.string().max(100, 'Máximo 100 caracteres').nullable(),
    justificativa: Yup.string().nullable(),
  });

const ParametrosLancamento = ({
  show,
  onClose,
  somenteLotesQuitados,
  processando,
  onLancar,
  onCancelar,
  onImportarExcel,
  temSelecionados,
  mostrarImportarExcel = true,
}) => {
  const [tiposReembolso, setTiposReembolso] = useState([]);
  const [loadingTipos, setLoadingTipos] = useState(true);

  useEffect(() => {
    let ativo = true;
    listarTiposReembolso()
      .then((res) => {
        if (!ativo) return;
        setTiposReembolso(
          (res || []).map((t) => ({ value: t.codigo, label: t.descricao }))
        );
      })
      .catch((err) => {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar tipos de reembolso';
        toast.error(msg);
      })
      .finally(() => {
        if (!ativo) return;
        setLoadingTipos(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const handleSubmit = (values) => {
    if (!temSelecionados) {
      toast.warn('Selecione ao menos um imóvel');
      return;
    }
    onLancar(values);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Formik
        initialValues={{
          codigoReembolso: '',
          primeiroVencimento: '',
          mensagem1: '',
          mensagem2: '',
          justificativa: '',
        }}
        validationSchema={parametrosSchema(somenteLotesQuitados)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit: submitForm }) => (
          <Form onSubmit={submitForm}>
            <Modal.Header closeButton>
              <Modal.Title>Parâmetros de Lançamento</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>
                      1º Vencimento
                      {somenteLotesQuitados && <span className="text-danger ml-1">*</span>}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      name="primeiroVencimento"
                      value={values.primeiroVencimento}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.primeiroVencimento && !!errors.primeiroVencimento}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.primeiroVencimento}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>
                      Tipo de Reembolso <span className="text-danger">*</span>
                    </Form.Label>
                    {loadingTipos ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <Select
                        styles={customStyles}
                        components={{ DropdownIndicator }}
                        options={tiposReembolso}
                        value={tiposReembolso.find((t) => t.value === values.codigoReembolso) || null}
                        onChange={(opt) => setFieldValue('codigoReembolso', opt ? opt.value : '')}
                        placeholder="Selecione..."
                        isClearable
                        noOptionsMessage={() => 'Nenhum tipo'}
                      />
                    )}
                    {touched.codigoReembolso && errors.codigoReembolso && (
                      <div className="invalid-feedback d-block">{errors.codigoReembolso}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mensagem 1</Form.Label>
                    <Form.Control
                      type="text"
                      name="mensagem1"
                      maxLength={100}
                      value={values.mensagem1}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.mensagem1 && !!errors.mensagem1}
                    />
                    <Form.Control.Feedback type="invalid">{errors.mensagem1}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mensagem 2</Form.Label>
                    <Form.Control
                      type="text"
                      name="mensagem2"
                      maxLength={100}
                      value={values.mensagem2}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.mensagem2 && !!errors.mensagem2}
                    />
                    <Form.Control.Feedback type="invalid">{errors.mensagem2}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Justificativa</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="justificativa"
                      rows={2}
                      value={values.justificativa}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              {mostrarImportarExcel && (
                <Button
                  variant="outline-primary"
                  onClick={onImportarExcel}
                  disabled={processando}
                >
                  Importar Excel
                </Button>
              )}
              <Button
                variant="outline-secondary"
                onClick={onCancelar}
                disabled={processando}
              >
                Cancelar
              </Button>
              <Button variant="success" type="submit" disabled={processando}>
                {processando ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
                Lançar
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ParametrosLancamento;
